










































































































































import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import BreadcrumbComponent from '@/_shared/components/layout/breadcrumb.component.vue';
import NoResults from '@/_shared/components/globals/no-results.component.vue';
import GenealogyViewService from '@/manage/services/genealogy-view/genealogy-view.service';
import GenealogyViewQuery from '@/manage/interface/genealogy-view/genealogy-view-query.interface';
import GenealogyViewEntity from '@/manage/entities/genealogy-view/genealogy-view.entity';
import GenealogyCardView from '@/manage/components/genealogy-view/card-view.component.vue';
import GenealogyViews from '@/manage/enums/genealogy-views.enum';
import GenealogyTableView from '@/manage/components/genealogy-view/table-view.component.vue';
import throttle from 'lodash/throttle';
import PaginationComponent from '@/_shared/components/pagination/pagination.component.vue';
import SearchComponent from '@/_shared/components/search/search.component.vue';
import GenealogyViewFilter from '@/manage/components/genealogy-view/filter.component.vue';
import ExcelComponent from '@/_shared/components/globals/excel.component.vue';
import ThemeService from '@/_shared/services/theme.service';
import InfoIcon from '@/_shared/components/icons/info-icon.component.vue';
import excelService from '@/_shared/services/excel.service';
import snakeCase from 'lodash/snakeCase';
import DateService from '@/_shared/services/date.service';
import reportsAllowedService from '@/_shared/services/reportsAllowed/reports-allowed.service';
import Genealogy from '@/manage/entities/genealogy-view/genealogy.entity';
import FilterIcon from '@/_shared/components/icons/filter-icon.component.vue';
import { BBadge } from 'bootstrap-vue';
import LogService from '@/_shared/services/log/log.service';

const MD_BREAKPOINT = 960;

@Component({
  components: {
    InfoIcon,
    ExcelComponent,
    FilterIcon,
    SearchComponent,
    PaginationComponent,
    CardView: GenealogyCardView,
    TableView: GenealogyTableView,
    NoResults,
    BreadcrumbComponent,
    GenealogyViewFilter,
    BBadge,
  },
})
export default class GenealogyView extends Vue {
  genealogyView: GenealogyViewEntity = new GenealogyViewEntity();
  busyLoading = false;
  currentView = GenealogyViews.CARD;
  isMobile = window.innerWidth < MD_BREAKPOINT;
  themeService = ThemeService;
  recordLimit = 1500;
  filterNumber = 0;
  query: GenealogyViewQuery = {
    q: '',
    page: 1,
    limit: 12,
    sort: 'name',
  }

  get showNoResults(): boolean {
    return !this.$store.state.LoadingStore.isActive && !this.genealogyView.results.length;
  }

  get showViewSwitch(): boolean {
    return !this.showNoResults && !this.isMobile;
  }

  get isCardView(): boolean {
    return this.currentView === GenealogyViews.CARD;
  }

  get showCardView(): boolean {
    return this.isCardView || this.isMobile;
  }

  get isExportDisabled(): boolean {
    return this.genealogyView.total > this.recordLimit;
  }

  get showExportWarning(): boolean {
    return this.isExportDisabled && !this.isMobile;
  }

  applySearchString(): void {
    this.query.page = 1;
    this.setGenealogyView();
  }

  changeSort(sort: string) {
    this.query.page = 1;
    this.query.sort = sort;
    this.setGenealogyView(true);
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  async handleChangePagination(): Promise<void> {
    await this.$nextTick();
    await this.setGenealogyView();
    this.scrollToTop();
  }

  toggleView(): void {
    this.currentView = this.isCardView ? GenealogyViews.TABLE : GenealogyViews.CARD;
  }

  setFilterNumber(filter: GenealogyViewQuery) {
    this.filterNumber = Object.keys(filter).length;
    if (filter.joinDateTo && filter.joinDateFrom) {
      this.filterNumber -= 1;
    }
  }

  applyFilter(filter: GenealogyViewQuery): void {
    this.setFilterNumber(filter);
    this.query = {
      ...filter,
      q: this.query.q,
      page: 1,
      limit: this.query.limit,
      sort: this.query.sort,
    };
    this.setGenealogyView();
  }

  getGenealogy(query?: GenealogyViewQuery): Promise<GenealogyViewEntity> {
    return GenealogyViewService.getGenealogy(query || this.query);
  }

  setGenealogyView(busyLoading = false): Promise<void> {
    this.toggleLoading(busyLoading);
    return this.getGenealogy()
      .then((response) => {
        this.genealogyView = response;
      })
      .catch(() => {
        this.genealogyView = new GenealogyViewEntity();
        this.query.page = 1;
        LogService.toastrError(
          this.$t('ERROR_HANDLER.MESSAGE').toString(),
          this.$t('ERROR_HANDLER.TRY_AGAIN').toString(),
        );
      })
      .finally(() => {
        this.toggleLoading(busyLoading);
      });
  }

  toggleLoading(busyLoading?: boolean): void {
    if (busyLoading) {
      this.busyLoading = !this.busyLoading;
      return;
    }
    if (this.$store.state.LoadingStore.isActive) {
      this.$store.dispatch('hideLoading');
      return;
    }
    this.$store.dispatch('showLoading');
  }

  setIsMobile(): void {
    this.isMobile = window.innerWidth < MD_BREAKPOINT;
  }

  async exportToExcel(): Promise<void> {
    const excelDetails = await this.mapFieldsToExcel();
    if (!excelDetails.length) return;
    const filename = snakeCase(this.$t('SIDEBAR.MANAGE.GENEALOGY_VIEW').toString());
    excelService.export(excelDetails, filename, true, '');
  }

  async mapFieldsToExcel(): Promise<Record<string, any>[]> {
    const excelRows: any = [];
    let records;
    this.toggleLoading();

    try {
      records = await this.getGenealogy({ ...this.query, limit: 1500, page: 1 }) || {};
    } catch (e) {
      LogService.toastrError(
        this.$t('ERROR_HANDLER.MESSAGE').toString(),
        this.$t('ERROR_HANDLER.TRY_AGAIN').toString(),
      );
      this.toggleLoading();
      return [];
    }

    (records.results || []).forEach((record) => {
      excelRows.push(this.createExcelRow(record));
    });

    this.toggleLoading();
    return excelRows;
  }

  createExcelRow(record: Genealogy): Record<string, any> {
    const fields = [
      { label: this.$t('GENEALOGY_VIEW.BP_NAME_ID'), value: `${record.name || '-'} / ${record.repDid || '-'}` },
      { label: this.$t('GENEALOGY_VIEW.CONVENTION_TICKET'), value: this.getWordStateByBoolean(record.conventionTicket) },
      { label: this.$t('GENEALOGY_VIEW.LIFETIME_RANK'), value: record.lifetimeRank.rankConstant.getRankName() },
      { label: this.$t('GENEALOGY_VIEW.CURRENT_MONTH_RANK'), value: record.currentMonthRank.rankConstant.getRankName() || '-', repAllow: 'downline_rank_comparison' },
      { label: this.$t('GENEALOGY_VIEW.LAST_MONTH_RANK'), value: record.lastMonthRank.rankConstant.getRankName() || '-', repAllow: 'downline_rank_comparison' },
      { label: this.$t('GENEALOGY_VIEW.CURRENT_PAYGATE'), value: record.currentPayGate, repAllow: 'downline_paygate_comparison' },
      { label: this.$t('GENEALOGY_VIEW.CURRENT_MONTH_CV'), value: record.currentMonthCv },
      { label: this.$t('GENEALOGY_VIEW.GROUP_QV'), value: record.groupQv },
      { label: this.$t('GENEALOGY_VIEW.JOIN_DATE'), value: DateService.getLocaleFormattedDate(record.joinDate) },
      { label: this.$t('GENEALOGY_VIEW.ACTIVE_SUBSCRIPTION'), value: this.getWordStateByBoolean(record.hasActiveSubscription) },
      { label: this.$t('GENEALOGY_VIEW.FAST_START'), value: record.fastStartProgress, repAllow: 'downline_fast_start' },
      { label: this.$t('GENEALOGY_VIEW.STATE'), value: record.state || '-' },
    ];

    const allowedFields = fields
      .filter((field) => !field.repAllow || reportsAllowedService.isAllowed(field.repAllow));
    const rowEntries = allowedFields.map((field) => ([field.label, field.value]));

    return Object.fromEntries(rowEntries);
  }

  getWordStateByBoolean(state: boolean): string {
    if (state) {
      return this.$t('GENERIC.LARGE.YES').toString();
    }
    return this.$t('GENERIC.LARGE.NO').toString();
  }

  setResizeEvent(): void {
    window.addEventListener('resize', throttle(this.setIsMobile, 300));
  }

  created() {
    this.setGenealogyView();
    this.setResizeEvent();
  }
}
